import type { LegalDocument } from "./legal-types";
import { LEGAL_COMPANY_CONTACT, LEGAL_COMPANY_NAME, LEGAL_DPO_EMAIL } from "./legal-meta";

const privacyPolicyPtBr: LegalDocument = {
  id: "privacy",
  title: "Política de Privacidade",
  disclaimer:
    "Este documento descreve como o aplicativo Innovally trata dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).",
  sections: [
    {
      id: "about",
      title: "Quem somos",
      audiences: ["ALL"],
      paragraphs: [
        `${LEGAL_COMPANY_NAME} é uma plataforma de delivery e gestão para restaurantes, lojas e operações de entrega, oferecendo ferramentas para lojistas, sublojistas e motoboys acompanharem pedidos, entregas e operação do estabelecimento.`,
      ],
    },
    {
      id: "controller",
      title: "Controlador dos dados",
      audiences: ["ALL"],
      paragraphs: [
        `O controlador dos dados pessoais tratados neste aplicativo é ${LEGAL_COMPANY_NAME}.`,
        `Para assuntos gerais sobre privacidade, utilize ${LEGAL_COMPANY_CONTACT}.`,
      ],
    },
    {
      id: "dpo",
      title: "Encarregado de dados (DPO)",
      audiences: ["ALL"],
      paragraphs: [
        `O encarregado pelo tratamento de dados pessoais pode ser contatado pelo e-mail ${LEGAL_DPO_EMAIL} para exercício de direitos previstos na LGPD.`,
      ],
    },
    {
      id: "data-collected-common",
      title: "Dados coletados — todos os perfis",
      audiences: ["ALL"],
      paragraphs: [
        "Independentemente do perfil de acesso, podemos tratar os seguintes dados quando você utiliza o aplicativo:",
      ],
      bullets: [
        "Nome, e-mail e função vinculada à conta fornecida pelo estabelecimento.",
        "Credenciais de autenticação (senha armazenada de forma segura no servidor; tokens de sessão armazenados localmente no dispositivo).",
        "Identificador do estabelecimento selecionado e preferências do app (ex.: tema claro ou escuro).",
        "Token de notificações push, quando você autoriza alertas no dispositivo.",
        "Registros técnicos de uso e logs de erro para segurança e suporte.",
      ],
    },
    {
      id: "data-collected-motoboy",
      title: "Dados coletados — Motoboy",
      audiences: ["MOTOBOY"],
      paragraphs: ["Ao utilizar o perfil de entregador, tratamos também:"],
      bullets: [
        "Localização geográfica (GPS) ao iniciar e concluir entregas, para registro operacional da rota.",
        "Dados de pedidos atribuídos: nome, telefone e endereço do cliente final, itens, valores e status da entrega.",
        "Horários de início e conclusão das entregas.",
        "Abertura de apps de navegação (Maps/Waze) com endereço de entrega — o tratamento nesses apps segue as políticas deles.",
      ],
    },
    {
      id: "data-collected-lojista",
      title: "Dados coletados — Lojista e Sub Lojista",
      audiences: ["LOJISTA", "SUB_LOJISTA"],
      paragraphs: ["Ao utilizar o painel administrativo, tratamos também:"],
      bullets: [
        "Dados do estabelecimento: nome, telefone, link do cardápio/PDV e configurações operacionais.",
        "Pedidos, pagamentos, relatórios e movimentações de caixa vinculados ao estabelecimento.",
        "Dados de clientes cadastrados pelo estabelecimento, incluindo CPF ou CNPJ quando informados.",
        "Fotos selecionadas na galeria do dispositivo para avatar ou cadastro de clientes (mediante permissão).",
        "Preferências de impressão (ex.: integração RawBT no Android).",
        "Sub Lojistas compartilham o mesmo escopo operacional do lojista, com permissões definidas pelo estabelecimento.",
      ],
    },
    {
      id: "purposes",
      title: "Finalidades do tratamento",
      audiences: ["ALL"],
      paragraphs: ["Utilizamos os dados para:"],
      bullets: [
        "Autenticar usuários e permitir acesso seguro ao aplicativo.",
        "Operar pedidos, entregas, atendimento e relatórios do estabelecimento.",
        "Enviar notificações sobre novos pedidos, entregas e alertas operacionais.",
        "Registrar localização em entregas, quando aplicável ao perfil Motoboy.",
        "Prestar suporte, melhorar estabilidade e segurança da plataforma.",
        "Cumprir obrigações legais e regulatórias aplicáveis.",
      ],
    },
    {
      id: "legal-basis",
      title: "Bases legais (LGPD)",
      audiences: ["ALL"],
      paragraphs: ["O tratamento de dados pessoais fundamenta-se, conforme o caso, em:"],
      bullets: [
        "Execução de contrato ou procedimentos preliminares (Art. 7º, V) — operação do serviço contratado pelo estabelecimento.",
        "Legítimo interesse (Art. 7º, IX) — segurança, prevenção a fraudes e melhoria do serviço, respeitados seus direitos.",
        "Consentimento (Art. 7º, I) — localização, notificações push, acesso à galeria de fotos e demais permissões solicitadas pelo dispositivo.",
        "Cumprimento de obrigação legal ou regulatória (Art. 7º, II), quando aplicável.",
      ],
    },
    {
      id: "sharing",
      title: "Compartilhamento de dados",
      audiences: ["ALL"],
      paragraphs: ["Podemos compartilhar dados com:"],
      bullets: [
        "Infraestrutura e API Innovally (api.innovally.com.br) para processamento dos serviços.",
        "Provedores de notificações push (ex.: Notifee) para entrega de alertas no dispositivo.",
        "Apps de mapas e navegação (Google Maps, Waze) ao abrir rotas de entrega — apenas o endereço necessário.",
        "WhatsApp e cliente de e-mail do dispositivo, quando o lojista compartilha link da loja.",
        "RawBT (Android), quando configurada impressão térmica — dados limitados ao comprovante.",
        "Autoridades públicas, quando exigido por lei ou ordem judicial.",
      ],
      footerParagraphs: [
        "Não vendemos dados pessoais. O compartilhamento ocorre apenas para as finalidades descritas nesta política.",
      ],
    },
    {
      id: "retention",
      title: "Armazenamento e retenção",
      audiences: ["ALL"],
      paragraphs: [
        "Tokens de autenticação ficam armazenados localmente no dispositivo (AsyncStorage) enquanto a sessão estiver ativa ou até logout.",
        "Dados operacionais são mantidos nos servidores Innovally pelo prazo necessário à prestação do serviço, cumprimento legal e resolução de disputas — prazos específicos devem ser confirmados com assessoria jurídica.",
        "Ao encerrar a conta ou remover o aplicativo, tokens locais são eliminados; dados no servidor seguem política de retenção do controlador.",
      ],
    },
    {
      id: "permissions",
      title: "Permissões do dispositivo",
      audiences: ["ALL"],
      paragraphs: ["O aplicativo pode solicitar as seguintes permissões:"],
      bullets: [
        "Localização (Motoboy): registrar início e conclusão de entregas.",
        "Notificações: alertas de novos pedidos e entregas.",
        "Galeria de fotos (Lojista/Sub Lojista): seleção de imagens para cadastros.",
        "Impressora RawBT (Android): emissão de comprovantes, quando configurado.",
      ],
    },
    {
      id: "rights",
      title: "Seus direitos como titular",
      audiences: ["ALL"],
      paragraphs: ["Nos termos da LGPD, você pode solicitar:"],
      bullets: [
        "Confirmação da existência de tratamento e acesso aos dados.",
        "Correção de dados incompletos, inexatos ou desatualizados.",
        "Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade.",
        "Portabilidade dos dados a outro fornecedor, quando aplicável.",
        "Revogação do consentimento e informação sobre compartilhamentos.",
        "Oposição a tratamentos baseados em legítimo interesse, quando cabível.",
        "Reclamação à Autoridade Nacional de Proteção de Dados (ANPD).",
      ],
      footerParagraphs: [
        `Envie solicitações para ${LEGAL_DPO_EMAIL}. Responderemos dentro dos prazos legais.`,
      ],
    },
    {
      id: "security",
      title: "Segurança",
      audiences: ["ALL"],
      paragraphs: [
        "Adotamos medidas técnicas e organizacionais para proteger dados pessoais, incluindo comunicação criptografada (HTTPS), controle de acesso por perfil e armazenamento seguro de tokens.",
        "Nenhum sistema é totalmente imune a riscos; em caso de incidente relevante, comunicaremos conforme exigido pela legislação.",
      ],
    },
    {
      id: "changes",
      title: "Alterações desta política",
      audiences: ["ALL"],
      paragraphs: [
        "Podemos atualizar esta Política de Privacidade periodicamente. A data da última atualização será exibida nesta página. Alterações relevantes poderão ser comunicadas por notificação in-app ou por outros meios adequados.",
      ],
    },
    {
      id: "contact",
      title: "Contato",
      audiences: ["ALL"],
      paragraphs: [
        `Dúvidas ou solicitações sobre privacidade: ${LEGAL_DPO_EMAIL}`,
        `Contato geral: ${LEGAL_COMPANY_CONTACT}`,
      ],
    },
  ],
};

export { privacyPolicyPtBr };
